import { CopyIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
} from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { type Account } from "@umami/chains";
import { useDynamicModalContext, useToggleMnemonic } from "@umami/components";

import { EyeIcon, EyeOffIcon } from "../../assets/icons";
import { usePasskey } from "../../hooks/usePasskey";
import { useColor } from "../../styles/useColor";
import { getIsMnemonicAccountType } from "../../utils/accounts";
import { ModalBackButton } from "../BackButton";
import { ModalCloseButton } from "../CloseButton";
import { CopyButton } from "../CopyButton";

type RevealSeedPhraseModalProps = {
  account: Account;
  secret: string;
};
export const RevealSeedPhraseModal = ({ account, secret }: RevealSeedPhraseModalProps) => {
  const color = useColor();
  const isMnemonicAccountType = getIsMnemonicAccountType(account);
  const placeholder = isMnemonicAccountType ? "•••• ".repeat(24) : "••••".repeat(31);

  const { isVisible, toggleMnemonic } = useToggleMnemonic();
  const { isPasskeyFlow, isPasskeyUser } = usePasskey();
  const { goBack } = useDynamicModalContext();

  hj.stateChange("reveal_seed_phrase");

  return (
    <ModalContent>
      <ModalHeader>
        <VStack gap="12px">
          <Heading size="xl">Your {isMnemonicAccountType ? "Seed Phrase" : "Secret Key"}</Heading>
          <Text maxWidth="340px" color={color("700")} fontWeight="400" size="md">
            Do not share this with anyone. Anyone with your{" "}
            {isMnemonicAccountType ? "seed phrase" : "secret key"} can steal your funds.
          </Text>
        </VStack>
        <ModalBackButton
          onBack={() => {
            if (!isPasskeyFlow || !isPasskeyUser) {
              goBack();
            }
          }}
        />
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody>
        <VStack gap="30px">
          <Box
            width="100%"
            minHeight="126px"
            border="1px solid"
            borderColor={color("300")}
            borderRadius="0.375rem"
          >
            <Text padding="8px 16px" fontSize="18px" userSelect="none">
              {isVisible ? secret : placeholder}
            </Text>
          </Box>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Flex gap="16px" width="100%" marginTop="10px">
          <Button
            gap="4px"
            display="flex"
            width="full"
            fontSize="14px"
            fontWeight="400"
            leftIcon={
              <Icon as={isVisible ? EyeOffIcon : EyeIcon} boxSize="18px" color={color("400")} />
            }
            onClick={toggleMnemonic}
            variant="ghost"
          >
            {isVisible ? "Hide" : "Show"} {isMnemonicAccountType ? "phrase" : "key"}
          </Button>
          <CopyButton
            gap="8px"
            width="full"
            fontSize="14px"
            isDisposable
            value={secret}
            variant="ghost"
          >
            <Icon as={CopyIcon} boxSize="18px" color={color("400")} />
            Copy to clipboard
          </CopyButton>
        </Flex>
      </ModalFooter>
    </ModalContent>
  );
};
